import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/apiClient";
import { EmployeeDirectoryEntry, FollowUpWorkloadRow, WorkloadRow } from "../lib/types";

// --- Employee directory (assignee/watcher/approver pickers) ---
// request 1253: every active person's @mention name (handle → full name), for showing @shafeek with the name on hover
export function useMentionHandles() {
  return useQuery({
    queryKey: ["mention-handles"],
    queryFn: async () => (await api.get<{ data: Array<{ userId: string; handle: string; name: string; fullName: string | null }> }>("/users/mention-handles")).data.data,
    staleTime: 5 * 60_000,
  });
}

export function useEmployeeDirectory(search = "") {
  return useQuery({
    queryKey: ["employees", search],
    queryFn: async () => (await api.get<{ data: EmployeeDirectoryEntry[] }>("/users/employees", { params: { search } })).data.data,
  });
}

// --- Departments / Teams (Team Workload filters — Section 30) ---
export function useDepartments() {
  return useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/users/departments")).data.data });
}
export function useTeams(departmentId?: string) {
  return useQuery({ queryKey: ["teams", departmentId], queryFn: async () => (await api.get("/users/teams", { params: { departmentId } })).data.data });
}

export function useUnlinkedEmployees(search = "") {
  return useQuery({
    queryKey: ["unlinked-employees", search],
    queryFn: async () => (await api.get("/users/employees/unlinked", { params: { search } })).data.data,
  });
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => (await api.post("/users/departments", { name })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departments"] }),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; departmentId?: string | null }) => (await api.post("/users/teams", payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["teams"] }),
  });
}

// --- Employees (HRMS records) — admin management (Section 31: this build
// creates them itself since there's no real external HRMS to sync from) ---
export function useAllEmployees(search = "") {
  return useQuery({
    queryKey: ["all-employees", search],
    queryFn: async () => (await api.get("/users/employees/all", { params: { search } })).data.data,
  });
}

export interface CreateEmployeeInput {
  fullName: string;
  workEmail: string;
  employeeCode?: string;
  jobTitle?: string;
  departmentId?: string | null;
  teamIds?: string[];
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEmployeeInput) => (await api.post("/users/employees", payload)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-employees"] });
      qc.invalidateQueries({ queryKey: ["unlinked-employees"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, ...payload }: { employeeId: string; fullName?: string; employeeCode?: string; workEmail?: string; jobTitle?: string | null; departmentId?: string | null; teamIds?: string[]; isActive?: boolean }) =>
      (await api.patch(`/users/employees/${employeeId}`, payload)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-employees"] });
      qc.invalidateQueries({ queryKey: ["unlinked-employees"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (employeeId: string) => (await api.delete(`/users/employees/${employeeId}`)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-employees"] });
      qc.invalidateQueries({ queryKey: ["unlinked-employees"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

// --- Users (admin) ---
export function useUsers(filters: { status?: string; search?: string } = {}) {
  return useQuery({ queryKey: ["users", filters], queryFn: async () => (await api.get("/users", { params: filters })).data.data });
}
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: unknown) => (await api.post("/users", payload)).data.data, onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }) });
}
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, ...payload }: { userId: string } & Record<string, unknown>) => (await api.patch(`/users/${userId}`, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
/** Permanent, irreversible delete — Super Admin only, enforced server-side. */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => (await api.delete(`/users/${userId}`)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["all-employees"] });
      qc.invalidateQueries({ queryKey: ["unlinked-employees"] });
    },
  });
}
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (userId: string) => api.post(`/users/${userId}/resend-invite`), onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }) });
}
export function useBulkImportUsers() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (users: unknown[]) => (await api.post("/users/bulk-import", { users })).data.data, onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }) });
}

export interface BulkActivateResult {
  succeeded: string[];
  failed: { id: string; error: string }[];
}

/** Admin sets the password directly and skips the emailed activation link. */
export function useAdminActivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => (await api.post(`/users/${userId}/activate`, { password })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** request 1197: Super Admin resets passwords — set new ones, or email reset links */
/** request 1239: Super Admin switches off someone's two-step sign-in */
export function useResetTwoStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => api.post(`/users/${userId}/reset-2fa`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useAdminResetPasswords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { mode: "set" | "email"; items?: Array<{ userId: string; password: string }>; userIds?: string[]; signOut?: boolean }) =>
      (await api.post<{ data: BulkActivateResult }>("/users/reset-passwords", body)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useBulkAdminActivateUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (activations: Array<{ userId: string; password: string }>) =>
      (await api.post<{ data: BulkActivateResult }>("/users/bulk-activate", { activations })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

// --- Roles & permissions ---
export function useRoles() {
  return useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data.data });
}
export function useUpdateRolePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { roleId: string; module: string; permission: string; scope: string }) =>
      (await api.patch(`/roles/${payload.roleId}/permissions`, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roles"] }),
  });
}

// --- Tags ---
export function useTags(search = "") {
  return useQuery({ queryKey: ["tags", search], queryFn: async () => (await api.get("/tags", { params: { search } })).data.data });
}
export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (payload: { name: string; color?: string }) => (await api.post("/tags", payload)).data.data, onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }) });
}
export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tagId, ...payload }: { tagId: string; name?: string; color?: string }) => (await api.patch(`/tags/${tagId}`, payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}
export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (tagId: string) => api.delete(`/tags/${tagId}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["tags"] }) });
}

// --- My Tasks ---
export function useMyTasks() {
  return useQuery({ queryKey: ["my-tasks"], queryFn: async () => (await api.get("/my-tasks")).data.data, refetchInterval: 20000 });
}

// --- Team workload ---
export function useTeamWorkload(filters: Record<string, unknown> = {}) {
  return useQuery({ queryKey: ["team-workload", filters], queryFn: async () => (await api.get<{ data: WorkloadRow[] }>("/team-workload", { params: filters })).data.data });
}
export function useEmployeeWorkloadDetail(employeeId: string | undefined) {
  return useQuery({
    queryKey: ["employee-workload", employeeId],
    queryFn: async () => (await api.get(`/team-workload/employee/${employeeId}`)).data.data,
    enabled: !!employeeId,
  });
}

export function useFollowUpWorkload(filters: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ["team-workload", "follow-ups", filters],
    queryFn: async () => (await api.get<{ data: FollowUpWorkloadRow[] }>("/team-workload/follow-ups", { params: filters })).data.data,
  });
}
export function useEmployeeFollowUpDetail(employeeId: string | undefined) {
  return useQuery({
    queryKey: ["team-workload", "follow-ups", "employee", employeeId],
    queryFn: async () => (await api.get(`/team-workload/follow-ups/employee/${employeeId}`)).data.data,
    enabled: !!employeeId,
  });
}

// --- Dashboard ---
export function useDashboard(filters: Record<string, unknown> = {}) {
  return useQuery({ queryKey: ["dashboard", filters], queryFn: async () => (await api.get("/dashboard", { params: filters })).data.data });
}
export function useDashboardTaskList(kind: string | null) {
  return useQuery({
    queryKey: ["dashboard-tasks", kind],
    queryFn: async () => (await api.get("/dashboard/tasks", { params: { kind } })).data.data,
    enabled: !!kind,
  });
}

// --- Notifications ---
export function useNotifications(page = 1) {
  return useQuery({ queryKey: ["notifications", page], queryFn: async () => (await api.get("/notifications", { params: { page } })).data, refetchInterval: 15000 });
}
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (id: string) => api.post(`/notifications/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
}
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async () => api.post("/notifications/mark-all-read"), onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }) });
}
export function useNotificationPreferences() {
  return useQuery({ queryKey: ["notification-preferences"], queryFn: async () => (await api.get("/notifications/preferences")).data.data });
}
export function useUpdateNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ event, ...payload }: { event: string; inApp: boolean; email: boolean }) => api.put(`/notifications/preferences/${event}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-preferences"] }),
  });
}

// --- Audit ---
export function useAuditLog(filters: Record<string, unknown> = {}) {
  return useQuery({ queryKey: ["audit", filters], queryFn: async () => (await api.get("/audit", { params: filters })).data });
}

// --- CRM/ERP linked records ---
export function useLinkedRecordSearch(query: string, type?: string) {
  return useQuery({
    queryKey: ["linked-records", query, type],
    queryFn: async () => (await api.get("/integrations/crm/records", { params: { q: query, type } })).data.data,
    enabled: query.length > 0,
  });
}

// --- HRMS integration ---
/** request 1313: counts and dashboards show what's waiting for this person; the Request page passes everything to see every step */
export function useHrmsLeaveRequests(opts: { enabled?: boolean; status?: "PENDING" | "APPROVED" | "REJECTED" | "ALL"; everything?: boolean } = {}) {
  return useQuery({
    queryKey: ["hrms-leave-requests", opts.status ?? "PENDING"],
    queryFn: async () => (await api.get("/integrations/hrms/leave-requests", { params: { status: opts.status ?? "PENDING" } })).data.data,
    enabled: opts.enabled ?? true,
    select: (rows: any[]) => (opts.everything ? rows : rows.filter((r) => r.canDecide)),
  });
}
/** request 1313: approves anyone's leave - includes people with reports, whatever their role */
export function useLeaveApprover() {
  const { data } = useQuery({ queryKey: ["leave-approver"], staleTime: 5 * 60_000, queryFn: async () => (await api.get<{ data: { approver: boolean } }>("/integrations/hrms/leave-requests/access")).data.data });
  return !!data?.approver;
}
export function useHrmsWorkload(leaveRequestId: string | undefined) {
  return useQuery({
    queryKey: ["hrms-workload", leaveRequestId],
    queryFn: async () => (await api.get(`/integrations/hrms/leave-requests/${leaveRequestId}/workload`)).data.data,
    enabled: !!leaveRequestId,
  });
}
export function useDecideLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: "APPROVED" | "REJECTED" }) => api.post(`/integrations/hrms/leave-requests/${id}/decision`, { decision }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hrms-leave-requests"] });
      qc.invalidateQueries({ queryKey: ["hr", "dashboard"] });
    },
  });
}

/** request 1081: change a leave's type / dates / reason — approved leave included (attendance is re-marked by the server) */
export function useEditLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; leaveType: string; startDate: string; endDate: string; reason?: string | null; advanceNote?: string }) =>
      (await api.patch(`/integrations/hrms/leave-requests/${id}`, body)).data.data as { overVacationBalance: number },
    onSuccess: () => {
      for (const key of ["hrms-leave-requests", "my-leave-requests", "my-leave-balance", "leave-summary"]) qc.invalidateQueries({ queryKey: [key] });
      qc.invalidateQueries({ queryKey: ["hr", "dashboard"] });
    },
  });
}

// --- My own leave applications (self-service) ---
export function useMyLeaveRequests() {
  return useQuery({ queryKey: ["my-leave-requests"], queryFn: async () => (await api.get("/integrations/hrms/leave-requests/mine")).data.data });
}
// request 1075: someone else's balance when applying on their behalf
export function useMyLeaveBalance(employeeId?: string, enabled = true, startDate?: string, excludeLeaveId?: string) {
  return useQuery({
    queryKey: ["my-leave-balance", employeeId ?? "me", startDate ?? "", excludeLeaveId ?? ""],
    enabled,
    // keep the figures while the dates change — but never show one person's balance for another (request 1084)
    placeholderData: (prev: any, prevQuery: any) => (prevQuery?.queryKey?.[1] === (employeeId ?? "me") ? prev : undefined),
    queryFn: async () => (await api.get("/integrations/hrms/leave-requests/balance", { params: { employeeId: employeeId || undefined, startDate: startDate || undefined, excludeLeaveId: excludeLeaveId || undefined } })).data.data,
  });
}
/** request 1083: one employee's vacation balance on a given day (Final Settlement → last working day) */
export function useVacationAsOf(employeeId: string | undefined, asOf: string | undefined) {
  return useQuery({
    queryKey: ["my-leave-balance", employeeId ?? "", "asOf", asOf ?? ""],
    enabled: !!employeeId,
    queryFn: async () => {
      const rows = (await api.get("/integrations/hrms/leave-requests/balance", { params: { employeeId, asOf: asOf || undefined } })).data.data as any[];
      return rows.find((r) => r.leaveType === "ANNUAL") as { entitlement: number; used: number; pending: number; remaining: number; serviceYears: number; serviceMonths: number; accrued: number; accruedBalance: number } | undefined;
    },
  });
}
export function useApplyForLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      /** request 1065: applying for another employee */
      onBehalfOfEmployeeId?: string;
      leaveType: string;
      startDate: string;
      endDate: string;
      reason?: string;
      handoverToEmployeeId?: string;
      handoverNotes?: string;
      files?: File[];
      /** request 1082: give vacation beyond the balance in advance (special consideration) */
      advance?: boolean;
      advanceNote?: string;
    }) => {
      const form = new FormData();
      form.append("leaveType", payload.leaveType);
      if (payload.onBehalfOfEmployeeId) form.append("onBehalfOfEmployeeId", payload.onBehalfOfEmployeeId);
      form.append("startDate", payload.startDate);
      form.append("endDate", payload.endDate);
      if (payload.reason) form.append("reason", payload.reason);
      if (payload.handoverToEmployeeId) form.append("handoverToEmployeeId", payload.handoverToEmployeeId);
      if (payload.handoverNotes) form.append("handoverNotes", payload.handoverNotes);
      if (payload.advance) {
        form.append("advance", "true");
        form.append("advanceNote", payload.advanceNote ?? "");
      }
      (payload.files ?? []).forEach((f) => form.append("files", f));
      return (await api.post("/integrations/hrms/leave-requests", form, { headers: { "Content-Type": "multipart/form-data" } })).data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-leave-requests"] });
      qc.invalidateQueries({ queryKey: ["my-leave-balance"] });
      qc.invalidateQueries({ queryKey: ["hrms-leave-requests"] });
    },
  });
}

export async function downloadLeaveAttachment(attachmentId: string, fileName: string) {
  const res = await api.get(`/integrations/hrms/leave-requests/attachments/${attachmentId}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// --- Exports (trigger file download) ---
export function buildExportUrl(path: string, params: Record<string, unknown> = {}) {
  const qs = new URLSearchParams(params as Record<string, string>).toString();
  return `${api.defaults.baseURL}${path}${qs ? `?${qs}` : ""}`;
}

export async function downloadExport(path: string, params: Record<string, unknown>, filename: string) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// --- Organisation & access (Settings → Organisation, Roles & Permissions → Access matrix) ---
export interface AccessMatrix {
  levels: string[];
  areas: Array<{ key: string; label: string; hint: string; levels: string[]; module: string | null }>;
  pageRules: import("../lib/access").PageRule[];
  actions: Array<{ key: string; label: string; levels: string[] }>;
  /** what is set on each role (area / page / action key → level); anything missing inherits */
  roles: Array<{ id: string; code: string; name: string; description: string | null; isSystem: boolean; people: number; values: Record<string, string> }>;
  /** each person: their roles and their own settings (which win over their roles) */
  people: Array<{ userId: string; name: string; designation: string | null; department: string | null; mainRoleId: string | null; roleIds: string[]; values: Record<string, string> }>;
}
export type AccessChanges = { roles?: Array<{ roleId: string; values: Record<string, string | null> }>; people?: Array<{ userId: string; values: Record<string, string | null> }> };
export function useAccessMatrix(enabled = true) {
  return useQuery({ queryKey: ["access-matrix"], queryFn: async () => (await api.get<{ data: AccessMatrix }>("/org/access-matrix")).data.data, enabled });
}
export function useSaveAccessMatrix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (changes: AccessChanges) => (await api.put<{ data: AccessMatrix }>("/org/access-matrix", changes)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-matrix"] });
      qc.invalidateQueries({ queryKey: ["roles"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export interface OrgPerson {
  id: string;
  employeeCode: string;
  fullName: string;
  designation: string | null;
  departmentId: string | null;
  reportsToId: string | null;
  teamIds: string[];
  userId: string | null;
  hasAvatar: boolean;
  avatarUpdatedAt: string | null;
  mainRoleId: string | null;
  roleIds: string[];
}
/** request 1086: what this person may see / change on the chart, part by part (null = everything) */
export interface OrgPerms {
  departments: { view: boolean; edit: boolean };
  teams: { view: boolean; edit: boolean };
  designations: { view: boolean; edit: boolean };
  reportingLines: { view: boolean; edit: boolean };
  roles: boolean;
}
export interface Organisation {
  perms?: OrgPerms | null;
  departments: Array<{ id: string; name: string; sortOrder: number }>;
  teams: Array<{ id: string; name: string; departmentId: string | null; managerId: string | null }>;
  roles: Array<{ id: string; code: string; name: string }>;
  people: OrgPerson[];
}
export function useOrganisation(enabled = true) {
  return useQuery({ queryKey: ["organisation"], queryFn: async () => (await api.get<{ data: Organisation }>("/org")).data.data, enabled });
}
function useOrgMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organisation"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["access-matrix"] });
    },
  });
}
export const useUpdateOrgPerson = () =>
  useOrgMutation(async (v: { employeeId: string; input: Partial<Pick<OrgPerson, "designation" | "departmentId" | "reportsToId" | "teamIds" | "mainRoleId" | "roleIds">> }) => (await api.patch(`/org/people/${v.employeeId}`, v.input)).data.data);
export const useSaveDepartment = () => useOrgMutation(async (v: { id?: string; name: string; sortOrder?: number }) => (v.id ? api.patch(`/org/departments/${v.id}`, v) : api.post("/org/departments", v)));
export const useDeleteDepartment = () => useOrgMutation(async (id: string) => api.delete(`/org/departments/${id}`));
export const useSaveTeam = () => useOrgMutation(async (v: { id?: string; name: string; departmentId?: string | null; managerId?: string | null }) => (v.id ? api.patch(`/org/teams/${v.id}`, v) : api.post("/org/teams", v)));
export const useDeleteTeam = () => useOrgMutation(async (id: string) => api.delete(`/org/teams/${id}`));

/** Settings → Roles & Permissions → Add role / rename / delete. */
function useRoleMutation<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of ["access-matrix", "roles", "organisation", "users"]) qc.invalidateQueries({ queryKey: [key] });
    },
  });
}
export const useCreateRole = () => useRoleMutation(async (v: { name: string; description?: string; copyFromRoleId?: string | null }) => (await api.post("/org/roles", v)).data.data);
export const useUpdateRole = () => useRoleMutation(async (v: { id: string; name?: string; description?: string | null }) => (await api.patch(`/org/roles/${v.id}`, v)).data.data);
export const useDeleteRole = () => useRoleMutation(async (id: string) => api.delete(`/org/roles/${id}`));

// ---------------------------------------------------------------- Leave summary (request 1068)

export interface LeaveSummary {
  year: number;
  vacationEntitlement: number;
  totals: { pending: { count: number; days: number }; approved: { count: number; days: number }; rejected: { count: number; days: number } };
  byType: Array<{ leaveType: string; approved: number; pending: number }>;
  byMonth: number[];
  onLeaveToday: Array<{ employee: string; leaveType: string; until: string }>;
  upcoming: Array<{ employee: string; leaveType: string; from: string; to: string; status: string; days: number }>;
  employees: Array<{
    employeeId: string;
    code: string;
    name: string;
    department: string | null;
    // request 1075: vacation earned from the joining date, carried forward
    joiningDate: string | null;
    serviceYears: number;
    vacationEarned: number;
    vacationUsedTotal: number;
    vacationPending: number;
    vacationUsed: number;
    vacationBalance: number;
    unpaid: number;
    maternity: number | null;
    other: number;
    pendingDays: number;
    pendingCount: number;
    next: { from: string; to: string; type: string; status: string } | null;
  }>;
}
export function useLeaveSummary(f: { year: number; departmentId?: string; employeeId?: string }) {
  return useQuery({
    queryKey: ["leave-summary", f],
    queryFn: async () => (await api.get<{ data: LeaveSummary }>("/integrations/hrms/leave-summary", { params: { year: f.year, departmentId: f.departmentId || undefined, employeeId: f.employeeId || undefined } })).data.data,
  });
}
